/**
 * useAgentChat — WebSocket-based chat hook using @boox/agent-sdk
 *
 * Drop-in replacement for useChatStream with same return API.
 * Advantages over SSE:
 *   - Bidirectional: cancel, undo, redo, switch agent without new HTTP request
 *   - Multi-device session sync (same session, multiple clients)
 *   - Auto-reconnect with exponential backoff + heartbeat
 *   - Precise execution cancellation via AbortController on server
 *
 * Enable via: localStorage.setItem('boox:chat-transport', 'ws')
 * Falls back to SSE automatically if WS connection fails.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { AgentClient } from '@boox/agent-sdk/browser';
import type { StreamEvent, AgentSession } from '@boox/agent-sdk/browser';
import { getToken } from '@/lib/api';
import { useChatContextStore } from '@/stores/chat-context-store';
import type {
  ChatMessage,
  ChatMode,
  ChatReplyLanguage,
  ThinkingStep,
  AgentInfo,
  AutonomousState,
  AutonomousEvaluation,
  ChatActionResult,
  ApprovalRecommendation,
  LayerInvocationEnvelope,
} from './useChatStream';
import { tryParseWidget, dbMsgToChat, mergeSyncedMessages } from './useChatStreamHelpers';
import { api } from '@/lib/api';
import { resolveDynamicRuntimeChatContext } from '@/lib/dynamic-runtime-context';

// ─── Singleton client ─────────────────────────────

let _client: AgentClient | null = null;
let _clientToken: string | null = null;

function getOrCreateClient(): AgentClient {
  const token = getToken() ?? '';
  // Reuse client if token hasn't changed
  if (_client && _clientToken === token) return _client;

  // WS URL resolution order:
  // 1. VITE_AGENT_WS_URL env var (explicit, e.g. "ws://localhost:4010/ws")
  // 2. Derive from current page origin (production: same host, /ws path)
  const envWsUrl = typeof import.meta !== 'undefined'
    ? (import.meta.env as Record<string, string>).VITE_AGENT_WS_URL
    : undefined;

  let wsUrl: string;
  if (envWsUrl) {
    wsUrl = envWsUrl;
  } else {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    wsUrl = `${proto}//${location.host}/ws`;
  }

  // Clean up previous client
  if (_client) {
    try { _client.disconnect(); } catch { /* ignore */ }
  }

  _client = new AgentClient({ url: wsUrl, token });
  _clientToken = token;
  return _client;
}

// ─── Hook ─────────────────────────────────────────

export function useAgentChat(convId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<ChatMode>(null);
  const [connectionState, setConnectionState] = useState<string>('disconnected');

  const streamingIdRef = useRef<string | null>(null);
  const sessionRef = useRef<AgentSession | null>(null);
  const cancelFnRef = useRef<(() => void) | null>(null);
  const clientRef = useRef<AgentClient | null>(null);

  // Track connection state
  useEffect(() => {
    const client = getOrCreateClient();
    clientRef.current = client;
    const handleState = (state: string) => {
      setConnectionState(state);
    };
    client.on('connection-state', handleState as (...args: unknown[]) => void);

    // Connect eagerly
    void client.connect().catch(() => { /* WS connect failed — will retry or fall back */ });

    return () => {
      client.off('connection-state', handleState as (...args: unknown[]) => void);
    };
  }, []);

  /** Load message history for current conversation */
  const loadMessages = useCallback(async () => {
    if (!convId) { setMessages([]); return; }
    setLoading(true);
    try {
      const res = await api.get<{ items: Record<string, unknown>[] }>(`/chat/${convId}/messages?limit=100`);
      setMessages((res.items ?? []).map(dbMsgToChat));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [convId]);

  /** Send a user message; streams AI reply via WebSocket */
  const sendMessage = useCallback(async (
    content: string,
    opts: { agentId?: string; attachmentKeys?: string[]; targetConvId?: string; responseLanguage?: ChatReplyLanguage } = {},
  ) => {
    const cid = opts.targetConvId ?? convId;
    if (!cid || streaming) return;

    const client = clientRef.current ?? getOrCreateClient();

    // Ensure connected
    if (client.getConnectionState() !== 'connected') {
      // Reset reconnect counter so we get a fresh attempt even if
      // the background reconnect loop previously exhausted all retries
      client.resetReconnect();
      try {
        await client.connect();
      } catch {
        // Fall back — caller should catch and try SSE
        throw new Error('WebSocket connection failed');
      }
    }

    // Optimistic UI: add user message
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
      attachmentKeys: opts.attachmentKeys,
    };
    setMessages(prev => [...prev, userMsg]);

    // Streaming placeholder
    const streamId = `stream-${Date.now()}`;
    streamingIdRef.current = streamId;
    const assistantPlaceholder: ChatMessage = {
      id: streamId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, assistantPlaceholder]);
    setStreaming(true);

    let accText = '';
    let accThinking = '';
    const accSteps: ThinkingStep[] = [];
    let accAutonomous: AutonomousState | undefined;
    let cancelled = false;

    // Cancel function — leverages WS bidirectional
    cancelFnRef.current = () => {
      cancelled = true;
      if (sessionRef.current) {
        void client.cancel(sessionRef.current.id).catch(() => {});
      }
    };

    try {
      // Create or resume session
      // For now, use convId as our session mapping key
      // The WS protocol uses its own session IDs — we map from convId
      if (!sessionRef.current || sessionRef.current.id !== cid) {
        try {
          const { session } = await client.resumeSession(cid);
          sessionRef.current = session;
        } catch {
          // Session doesn't exist — create new
          const session = await client.createSession({
            tenantId: '', // Server resolves from auth token
            agentId: opts.agentId,
            title: content.slice(0, 100),
          });
          sessionRef.current = session;
        }
      }

      const sessionId = sessionRef.current.id;

      const chatContextState = useChatContextStore.getState();
      const dynamicRuntimeContext = await resolveDynamicRuntimeChatContext({
        pageContext: chatContextState.pageContext,
        question: content,
      });

      // Stream via async generator
      const stream = client.sendMessage(sessionId, content, opts.attachmentKeys?.map(k => ({
        key: k,
        filename: k,
        mimeType: 'application/octet-stream',
      })), {
        chatSpace: chatContextState.activeSpace,
        pageContext: chatContextState.pageContext,
        dynamicRuntimeContext,
        responseLanguage: opts.responseLanguage,
      });

      for await (const evt of stream) {
        if (cancelled) break;
        handleStreamEvent(evt);
      }
    } catch (err) {
      if (cancelled) {
        // User cancelled — clear thinking steps
        setMessages(prev =>
          prev.map(m =>
            m.id === streamId
              ? { ...m, thinkingSteps: undefined, autonomousState: m.autonomousState ? { ...m.autonomousState, active: false } : undefined }
              : m,
          ),
        );
      } else {
        setMessages(prev =>
          prev.map(m =>
            m.id === streamId
              ? { ...m, content: `⚠️ ${err instanceof Error ? err.message : 'Connection error'}` }
              : m,
          ),
        );
      }
    } finally {
      setStreaming(false);
      streamingIdRef.current = null;
      cancelFnRef.current = null;

      // Persist WS messages to platform-api DB so loadMessages can retrieve them
      try {
        if (cid && (content || accText)) {
          await api.post(`/chat/${cid}/messages/ws-sync`, {
            userContent: content || undefined,
            assistantContent: accText || undefined,
          });
        }
      } catch { /* best-effort persistence */ }

      // Sync from DB to get canonical message IDs and timestamps
      try {
        if (cid) {
          const res = await api.get<{ items: Record<string, unknown>[] }>(`/chat/${cid}/messages?limit=100`);
          const dbMessages = (res.items ?? []).map(dbMsgToChat);
          if (dbMessages.length > 0) {
            setMessages(prev => mergeSyncedMessages(prev, dbMessages, streamId));
          }
        }
      } catch { /* best-effort sync */ }
    }

    return accText;

    // ─── Event handler (closure over accumulators) ──────────────

    function handleStreamEvent(evt: StreamEvent) {
      switch (evt.type) {
        case 'text-delta':
          accText += evt.text;
          setMessages(prev =>
            prev.map(m =>
              m.id === streamId
                ? { ...m, content: accText, widgetData: tryParseWidget(accText), thinkingSteps: accSteps.length > 0 ? [...accSteps] : undefined }
                : m,
            ),
          );
          break;

        case 'tool-call-start':
          accThinking = '';
          accSteps.push({
            type: 'tool_call',
            text: `Calling ${evt.toolName ?? 'tool'}…`,
            toolName: evt.toolName,
            timestamp: new Date().toISOString(),
          });
          updateThinkingSteps();
          break;

        case 'tool-result':
          accSteps.push({
            type: 'tool_result',
            text: evt.isError ? `${(evt as Record<string, unknown>).callId ?? 'tool'} failed` : `tool done`,
            isError: evt.isError,
            timestamp: new Date().toISOString(),
          });
          updateThinkingSteps();
          break;

        case 'message-complete':
          // Final message — update content
          if (evt.message) {
            accText = evt.message.content;
            setMessages(prev =>
              prev.map(m =>
                m.id === streamId
                  ? { ...m, content: accText, widgetData: tryParseWidget(accText) }
                  : m,
              ),
            );
          }
          break;

        case 'session-update':
          // Update session metadata
          if (evt.session && sessionRef.current) {
            sessionRef.current = { ...sessionRef.current, ...evt.session };
          }
          break;

        case 'error':
          accText += `⚠️ ${evt.message}`;
          setMessages(prev =>
            prev.map(m =>
              m.id === streamId ? { ...m, content: accText } : m,
            ),
          );
          break;

        case 'done':
        case 'ping':
          break;

        default:
          // Handle extended Boox events that aren't in the base SDK protocol
          handleExtendedEvent(evt as unknown as Record<string, unknown>);
      }
    }

    function handleExtendedEvent(evt: Record<string, unknown>) {
      const type = evt.type as string;

      if (type === 'thinking' && typeof evt.text === 'string') {
        accThinking += evt.text;
        const existingIdx = accSteps.findIndex(s => s.type === 'thinking' && !s.toolName);
        if (existingIdx >= 0) {
          accSteps[existingIdx] = { ...accSteps[existingIdx]!, type: 'thinking', text: accThinking };
        } else {
          accSteps.push({ type: 'thinking', text: accThinking, timestamp: new Date().toISOString() });
        }
        updateThinkingSteps();
      } else if (type === 'agent_info') {
        const info: AgentInfo = {
          agentKey: (evt.agentKey as string) ?? '',
          templateSlug: (evt.templateSlug as string) ?? '',
          name: (evt.name as string) ?? '',
          nameZh: evt.nameZh as string | undefined,
          role: (evt.role as string) ?? '',
          department: (evt.department as string) ?? '',
        };
        setMessages(prev =>
          prev.map(m => m.id === streamId ? { ...m, agentInfo: info } : m),
        );
      } else if (type === 'layer_invocation_envelope') {
        const envelope = evt.envelope as LayerInvocationEnvelope | undefined;
        if (envelope) {
          setMessages(prev =>
            prev.map(m => m.id === streamId ? { ...m, layerInvocationEnvelope: envelope } : m),
          );
        }
      } else if (type === 'approval_recommendation') {
        const recommendation = evt.recommendation as ApprovalRecommendation | undefined;
        if (recommendation) {
          setMessages(prev =>
            prev.map(m => m.id === streamId ? { ...m, approvalRecommendation: recommendation } : m),
          );
        }
      } else if (type === 'mode' && typeof evt.mode === 'string') {
        setMode(evt.mode as ChatMode);
      } else if (type === 'action_result') {
        const actionResult: ChatActionResult = {
          actionType: (evt.actionType as string) ?? '',
          status: (evt.status as string) ?? '',
          jobId: evt.jobId as string | undefined,
          jobNumber: evt.jobNumber as string | undefined,
          attachmentIds: (evt.attachmentIds as string[]) ?? [],
          attachmentKey: (evt.attachmentKey as string | null) ?? null,
          attachmentMimeType: (evt.attachmentMimeType as string | null) ?? null,
          navigationHint: (evt.navigationHint as ChatActionResult['navigationHint']) ?? null,
          extracted: (evt.extracted as Record<string, string>) ?? null,
          missingFields: (evt.missingFields as string[]) ?? [],
        };
        window.dispatchEvent(new CustomEvent('boox:chat-action', { detail: actionResult }));
      } else if (type === 'autonomous_start') {
        accAutonomous = {
          active: true,
          goalDescription: (evt.goalDescription as string) ?? '',
          maxRounds: (evt.maxRounds as number) ?? 3,
          currentRound: 0,
          evaluations: [],
        };
        updateAutonomousState();
      } else if (type === 'autonomous_round' && accAutonomous) {
        accAutonomous = { ...accAutonomous, currentRound: (evt.round as number) ?? accAutonomous.currentRound };
        accThinking = '';
        updateAutonomousState();
      } else if (type === 'autonomous_eval' && accAutonomous) {
        const evaluation: AutonomousEvaluation = {
          round: (evt.round as number) ?? 0,
          isComplete: (evt.isComplete as boolean) ?? false,
          confidence: (evt.confidence as number) ?? 0,
          reflection: (evt.reflection as string) ?? '',
          suggestedAction: (evt.suggestedAction as AutonomousEvaluation['suggestedAction']) ?? 'continue',
        };
        accAutonomous = { ...accAutonomous, evaluations: [...accAutonomous.evaluations, evaluation] };
        updateAutonomousState();
      } else if (type === 'autonomous_complete' && accAutonomous) {
        accAutonomous = { ...accAutonomous, active: false, completed: (evt.completed as boolean) ?? true, totalRounds: (evt.rounds as number) ?? accAutonomous.currentRound };
        updateAutonomousState();
      } else if (type === 'autonomous_error' && accAutonomous) {
        accAutonomous = { ...accAutonomous, active: false, error: (evt.error as string) ?? 'Unknown error' };
        updateAutonomousState();
      }
    }

    function updateThinkingSteps() {
      setMessages(prev =>
        prev.map(m =>
          m.id === streamId ? { ...m, thinkingSteps: [...accSteps] } : m,
        ),
      );
    }

    function updateAutonomousState() {
      setMessages(prev =>
        prev.map(m =>
          m.id === streamId ? { ...m, autonomousState: accAutonomous ? { ...accAutonomous } : undefined } : m,
        ),
      );
    }
  }, [convId, streaming]);

  /** Upload a file and return its storage key */
  const uploadFile = useCallback(async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
    );
    const res = await api.post<{ key: string }>('/upload/direct', {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      data: base64,
      entityType: 'chat',
    });
    return res.key;
  }, []);

  /** Cancel the in-flight stream (via WebSocket cancel command) */
  const cancelStream = useCallback(() => {
    cancelFnRef.current?.();
  }, []);

  return { messages, streaming, loading, mode, connectionState, loadMessages, sendMessage, uploadFile, cancelStream };
}
